export interface HowIEatSpace {
  id: string
  _id?: string
  name: string
  userId: string
  owner?: {
    id: string
    _id?: string
    name: string
    email?: string
  }
  createdAt: string
  updatedAt: string
}

export interface HowIEatProfile {
  id: string
  userId: string
  dailyCalorieGoal: number
  dailyProteinGoalG: number
  dailyCarbsGoalG: number
  dailyFatsGoalG: number
  dailyWaterGoalMl: number
  goalWeightKg: number | null
  heightCm: number | null
  reminderEmailEnabled: boolean
  reminderHour: number
  fastingTargetHours: number
  updatedAt: string
}

export interface HowIEatMealItem {
  id?: string
  foodItemId?: string
  name: string
  quantity: number
  unit: string
  calories: number
  proteinG: number
  carbsG: number
  fatsG: number
}

export interface HowIEatMeal {
  id: string
  mealType: string
  entryDate: string
  entryTime: string
  notes: string | null
  totalCalories: number
  totalProteinG: number
  totalCarbsG: number
  totalFatsG: number
  items: HowIEatMealItem[]
  createdAt: string
}

export interface HowIEatToday {
  date: string
  goals: {
    calories: number
    proteinG: number
    carbsG: number
    fatsG: number
    waterMl: number
  }
  totals: {
    calories: number
    proteinG: number
    carbsG: number
    fatsG: number
    waterMl: number
  }
  streakDays: number
  goalWeightKg: number | null
  latestWeightKg: number | null
  meals: HowIEatMeal[]
}

export const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'] as const
export type MealType = (typeof MEAL_TYPES)[number]

export const FOOD_UNITS = ['g', 'ml', 'oz', 'serving', 'piece'] as const
export type FoodUnit = (typeof FOOD_UNITS)[number]

export interface HowIEatWeightEntry {
  id: string
  entryDate: string
  weightKg: number
  notes: string | null
  createdAt: string
}

export interface HowIEatFoodItem {
  id: string
  name: string
  brand: string | null
  barcode: string | null
  caloriesPer100g: number
  proteinPer100g: number
  carbsPer100g: number
  fatsPer100g: number
  defaultServingG: number
  isUserCreated: boolean
  source: string
}

export interface HowIEatWeekProgress {
  startDate: string
  endDate: string
  goals: {
    calories: number
    proteinG: number
    carbsG: number
    fatsG: number
    waterMl: number
  }
  days: Array<{
    date: string
    totals: {
      calories: number
      proteinG: number
      carbsG: number
      fatsG: number
      waterMl: number
    }
    mealCount: number
    hasLog: boolean
  }>
  averages: {
    calories: number
    proteinG: number
    carbsG: number
    fatsG: number
    waterMl: number
  }
  macroBreakdown: {
    proteinG: number
    carbsG: number
    fatsG: number
    proteinPct: number
    carbsPct: number
    fatsPct: number
  }
  weightTrend: Array<{ date: string; weightKg: number | null }>
  loggedDays: number
}

export function mealItemFromFood(food: HowIEatFoodItem, quantityG?: number): HowIEatMealItem {
  const qty = quantityG ?? food.defaultServingG
  const factor = qty / 100
  const round = (n: number) => Math.round(n * 10) / 10
  return {
    foodItemId: food.id,
    name: food.brand ? `${food.name} (${food.brand})` : food.name,
    quantity: qty,
    unit: 'g',
    calories: round(food.caloriesPer100g * factor),
    proteinG: round(food.proteinPer100g * factor),
    carbsG: round(food.carbsPer100g * factor),
    fatsG: round(food.fatsPer100g * factor),
  }
}

export interface HowIEatMealTemplate {
  id: string
  name: string
  mealType: string
  items: HowIEatMealItem[]
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface HowIEatFastingStatus {
  active: boolean
  targetHours: number
  session: {
    id: string
    startedAt: string
    targetHours: number
    elapsedHours: number
    remainingHours: number
    progressPct: number
  } | null
}

export interface HowIEatFastingHistoryEntry {
  id: string
  startedAt: string
  endedAt: string | null
  targetHours: number
  completed: boolean
  durationHours: number | null
  goalMet: boolean
}

export interface HowIEatAchievement {
  key: string
  title: string
  description: string
  icon: string
  unlocked: boolean
  unlockedAt: string | null
}

export interface MenuItemNutritionPreview {
  id: string
  name: string
  description: string | null
  calories: number | null
  proteinG: number | null
  carbsG: number | null
  fatsG: number | null
  hasNutrition: boolean
}

export interface FoodPhotoAnalysisItem extends HowIEatMealItem {
  confidence?: number
}

export interface FoodPhotoAnalysisResult {
  items: FoodPhotoAnalysisItem[]
  notes?: string
  overallConfidence?: number
}
